/** Modelos partilhados: o caminho dos bytes entre o browser e o servidor.
 *
 *  Por agora os ficheiros ficam na base de dados da plataforma (Neon,
 *  plano gratuito), que tem pouco espaço. Por isso:
 *
 *   - o IFC é comprimido AQUI, no browser (gzip, com o CompressionStream
 *     nativo), antes de sair. Os limites contam o tamanho comprimido;
 *   - vai em blocos (4 MB, o tamanho vem do servidor), um pedido por
 *     bloco. Um envio interrompido retoma-se: o servidor diz que blocos
 *     faltam;
 *   - a descarga também é em blocos; no fim descomprime-se e confirma-se
 *     o SHA-256 antes de abrir. O que não bater certo não abre.
 */
import { ApiError, NetworkError, api, mensagemDoErro } from '@core/api/client'
import type {
  EstadoModelosServidor,
  ModeloPartilhado,
  PedidoDeDescarga,
  PedidoDeEnvio,
} from './modelosRegras'
import type { ModeloNaReciclagem } from './reciclagemRegras'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''
const BASE = '/api/modules/modelos'
/** Tentativas por bloco antes de desistir. Uma falha de rede a meio de
 *  um envio de 40 blocos não deve obrigar a recomeçar. */
const TENTATIVAS = 3

export type AoProgredir = (feito: number, total: number) => void

export const modelosApi = {
  estado: () => api.get<EstadoModelosServidor>(`${BASE}/estado`),
  listar: () => api.get<ModeloPartilhado[]>(BASE),
  /** As revisões de um modelo partilhado com esta pessoa, de qualquer
   *  workspace ("Partilhados comigo"). */
  revisoesDaPartilha: (partilhaId: string) =>
    api.get<ModeloPartilhado[]>(`${BASE}/partilha/${encodeURIComponent(partilhaId)}`),
  /** Vai para a Reciclagem (7 dias); restaura-se com `restaurar`. */
  apagar: (id: string) => api.delete<void>(`${BASE}/${id}`),
  /** As revisões deste workspace na Reciclagem que a pessoa pode restaurar. */
  reciclagem: () => api.get<ModeloNaReciclagem[]>(`${BASE}/reciclagem`),
  restaurar: (id: string) => api.post<void>(`${BASE}/${encodeURIComponent(id)}/restaurar`),
  definirQuota: (quotaMb: number | null) =>
    api.put<EstadoModelosServidor>(`${BASE}/quota`, { quota_mb: quotaMb }),
}

export async function sha256Hex(dados: ArrayBuffer): Promise<string> {
  const h = await crypto.subtle.digest('SHA-256', dados)
  return Array.from(new Uint8Array(h))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export async function comprimirGzip(dados: Blob): Promise<Blob> {
  const fluxo = dados.stream().pipeThrough(new CompressionStream('gzip'))
  return await new Response(fluxo).blob()
}

export async function descomprimirGzip(dados: Blob): Promise<ArrayBuffer> {
  const fluxo = dados.stream().pipeThrough(new DecompressionStream('gzip'))
  return await new Response(fluxo).arrayBuffer()
}

function esperar(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

/** Um pedido de bloco, com novas tentativas para falhas de rede e erros
 *  5xx. Um 4xx é uma recusa e não se repete. (Também o usam os blocos
 *  das sessões partilhadas.) */
export async function pedidoDeBloco(url: string, init: RequestInit): Promise<Response> {
  let ultimoErro: unknown = null
  for (let t = 0; t < TENTATIVAS; t++) {
    if (t > 0) await esperar(500 * 2 ** (t - 1))
    let res: Response
    try {
      res = await fetch(`${API_BASE_URL}${url}`, { credentials: 'include', ...init })
    } catch (err) {
      ultimoErro = new NetworkError(err)
      continue
    }
    if (res.ok) return res
    const tipo = res.headers.get('content-type') ?? ''
    const corpo: unknown = tipo.includes('application/json')
      ? await res.json().catch(() => null)
      : await res.text().catch(() => '')
    const erro = new ApiError(res.status, corpo, mensagemDoErro(res.status, corpo))
    if (res.status < 500) throw erro
    ultimoErro = erro
  }
  throw ultimoErro ?? new Error('O pedido falhou.')
}

export interface EnvioIn {
  nome: string
  /** O IFC tal como o viewer o abriu (o SHA-256 é destes bytes). */
  ficheiro: Blob
  sha256: string
  schemaIfc: string | null
  /** Para um nome novo: o workspace inteiro vê-o logo (o que se fazia
   *  antes). Uma revisão de um nome que já existe fica com a lista de
   *  acessos que esse nome já tem. */
  workspaceInteiro?: boolean
  aoProgredir?: AoProgredir
}

/** Comprime, envia os blocos que faltam e confirma. Devolve o modelo
 *  já disponível para os outros. */
export async function enviarModelo(e: EnvioIn): Promise<ModeloPartilhado> {
  const gz = await comprimirGzip(e.ficheiro)
  const pedido = await api.post<PedidoDeEnvio>(`${BASE}/envios`, {
    nome: e.nome,
    sha256: e.sha256,
    tamanho: e.ficheiro.size,
    tamanho_comprimido: gz.size,
    schema_ifc: e.schemaIfc,
    workspace_inteiro: e.workspaceInteiro ?? false,
  })
  const total = pedido.numero_de_blocos
  let feitos = total - pedido.em_falta.length
  e.aoProgredir?.(feitos, total)
  for (const i of pedido.em_falta) {
    const bloco = gz.slice(i * pedido.bloco_bytes, (i + 1) * pedido.bloco_bytes)
    await pedidoDeBloco(`${BASE}/${pedido.id}/blocos/${i}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: bloco,
    })
    feitos++
    e.aoProgredir?.(feitos, total)
  }
  return await api.post<ModeloPartilhado>(`${BASE}/${pedido.id}/confirmar`)
}

export class ModeloCorrompido extends Error {}

/** Descarrega os blocos, descomprime e confirma o SHA-256. Devolve os
 *  bytes do IFC, prontos a abrir como um ficheiro local. */
export async function descarregarModelo(
  id: string,
  aoProgredir?: AoProgredir,
): Promise<{ info: PedidoDeDescarga; bytes: ArrayBuffer }> {
  const info = await api.get<PedidoDeDescarga>(`${BASE}/${id}/descarga`)
  const partes: ArrayBuffer[] = []
  aoProgredir?.(0, info.numero_de_blocos)
  for (let i = 0; i < info.numero_de_blocos; i++) {
    const res = await pedidoDeBloco(`${BASE}/${id}/blocos/${i}`, { method: 'GET' })
    partes.push(await res.arrayBuffer())
    aoProgredir?.(i + 1, info.numero_de_blocos)
  }
  const bytes = await descomprimirGzip(new Blob(partes))
  if (bytes.byteLength !== info.tamanho || (await sha256Hex(bytes)) !== info.sha256) {
    throw new ModeloCorrompido(
      `O ficheiro recebido de "${info.nome}" não corresponde ao registado `
        + '(tamanho ou SHA-256 diferente). Não foi aberto.',
    )
  }
  return { info, bytes }
}
